import React from "react";
import { VideoRef } from "../mockData";

export function videoEmbedUrl(v: VideoRef, opts?: { autoplay?: boolean }): string {
  const ap = !!opts?.autoplay;
  switch (v.platform) {
    case "youtube":
      // Browsers block unmuted autoplay — request autoplay anyway; user-gesture will start audio.
      return `https://www.youtube-nocookie.com/embed/${v.id}?rel=0${ap ? "&autoplay=1&playsinline=1" : ""}`;
    case "tiktok":
      return `https://www.tiktok.com/embed/v2/${v.id}${ap ? "?autoplay=1" : ""}`;
    case "instagram":
      return `https://www.instagram.com/p/${v.id}/embed/${ap ? "?autoplay=1" : ""}`;
  }
}

export function videoCanonicalUrl(v: VideoRef): string {
  switch (v.platform) {
    case "youtube":
      return `https://www.youtube.com/watch?v=${v.id}`;
    case "tiktok":
      return v.handle
        ? `https://www.tiktok.com/@${v.handle}/video/${v.id}`
        : `https://www.tiktok.com/embed/v2/${v.id}`;
    case "instagram":
      return `https://www.instagram.com/p/${v.id}/`;
  }
}

export function videoThumbUrl(v: VideoRef): string | null {
  // Only YouTube exposes a public thumbnail URL we can use without API keys.
  if (v.platform === "youtube") {
    return `https://i.ytimg.com/vi/${v.id}/hqdefault.jpg`;
  }
  return null;
}

const PLATFORM_LABEL: Record<VideoRef["platform"], string> = {
  youtube: "YouTube",
  tiktok: "TikTok",
  instagram: "Instagram",
};

export function PlatformBadge({ platform, className = "" }: { platform: VideoRef["platform"]; className?: string }) {
  const tone =
    platform === "youtube"   ? "bg-red-50    text-red-700    ring-red-200"   :
    platform === "tiktok"    ? "bg-slate-900 text-white      ring-slate-900" :
    /* instagram */            "bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200";
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[0.65rem] font-semibold ring-1 ${tone} ${className}`}>
      {PLATFORM_LABEL[platform]}
    </span>
  );
}

/** A platform-aware iframe wrapper. YouTube → 16:9. TikTok / Instagram → portrait. */
export function VideoEmbed({
  video,
  title,
  className = "",
  size = "default",
  autoplay = false,
}: {
  video: VideoRef;
  title?: string;
  className?: string;
  /** "default" for detail pages, "compact" for listing cards. */
  size?: "default" | "compact";
  /** Request autoplay (browsers may force-mute). */
  autoplay?: boolean;
}) {
  const isPortrait = video.platform !== "youtube";
  // TikTok/IG embeds include their own chrome (header, caption, share buttons).
  // Use a fixed height tall enough that the iframe content fits without scrollbars,
  // and a wider max-width than raw 9:16 so the player doesn't feel cramped.
  const portraitDims = size === "compact"
    ? "max-w-[360px] h-[640px]"
    : "max-w-[460px] h-[760px]";
  const wrapClass = isPortrait
    ? `${portraitDims} w-full mx-auto`
    : "aspect-video w-full";
  return (
    <div className={`${wrapClass} bg-black overflow-hidden rounded-xl ${className}`}>
      <iframe
        className="w-full h-full"
        src={videoEmbedUrl(video, { autoplay })}
        title={title ?? "video"}
        loading="lazy"
        scrolling="no"
        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; autoplay; fullscreen"
        allowFullScreen
      />
    </div>
  );
}
